import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import {
  isSelectableCatalogSourceCandidate,
  type CatalogSourceCandidate,
} from "@/types/componentDataBinding";
import { cn } from "@/lib/utils";

type CatalogPickerContextType = "project" | "organization" | "personal";

const CATALOG_PICKER_CONTEXT_ORDER: CatalogPickerContextType[] = [
  "project",
  "organization",
  "personal",
];

const CATALOG_PICKER_CONTEXT_LABELS: Record<CatalogPickerContextType, string> =
  {
    project: "Project",
    organization: "Organization",
    personal: "Personal",
  };

interface CatalogSourcePickerProps {
  candidates: CatalogSourceCandidate[];
  selectedCandidateKey: string | null;
  isLoading: boolean;
  error: string | null;
  isCreatingPmSource: boolean;
  createPmSourceError: string | null;
  canCreatePmSource: boolean;
  onCreatePmSource: () => void;
  onSelectCandidate: (candidate: CatalogSourceCandidate) => void;
  onClearSelection: () => void;
}

export function getCatalogSourceCandidateKey(
  candidate: CatalogSourceCandidate,
): string {
  const { source } = candidate;
  return [
    source.dataOwnerOrganizationId,
    source.ownerType,
    source.ownerId,
    source.groupId,
    source.schemaId,
  ].join(":");
}

function getCandidateContextType(
  candidate: CatalogSourceCandidate,
): CatalogPickerContextType {
  return candidate.source.ownerType === "user"
    ? "personal"
    : candidate.source.ownerType;
}

function getCandidateLabel(candidate: CatalogSourceCandidate): string {
  return candidate.labels.schemaDisplayName || candidate.labels.schemaName;
}

export const CatalogSourcePicker: React.FC<CatalogSourcePickerProps> = ({
  candidates,
  selectedCandidateKey,
  isLoading,
  error,
  isCreatingPmSource,
  createPmSourceError,
  canCreatePmSource,
  onCreatePmSource,
  onSelectCandidate,
  onClearSelection,
}) => {
  const [activeContext, setActiveContext] =
    useState<CatalogPickerContextType>("organization");

  const selectableCandidates = useMemo(
    () => candidates.filter(isSelectableCatalogSourceCandidate),
    [candidates],
  );

  const candidatesByContext = useMemo(() => {
    const grouped: Record<CatalogPickerContextType, CatalogSourceCandidate[]> =
      {
        project: [],
        organization: [],
        personal: [],
      };

    for (const candidate of selectableCandidates) {
      grouped[getCandidateContextType(candidate)].push(candidate);
    }

    return grouped;
  }, [selectableCandidates]);

  const availableContexts = useMemo(
    () =>
      CATALOG_PICKER_CONTEXT_ORDER.filter(
        (context) => candidatesByContext[context].length > 0,
      ),
    [candidatesByContext],
  );

  const visibleActiveContext = availableContexts.includes(activeContext)
    ? activeContext
    : availableContexts[0];

  const selectedCandidate = useMemo(() => {
    if (selectedCandidateKey === null) {
      return undefined;
    }

    return selectableCandidates.find(
      (candidate) =>
        getCatalogSourceCandidateKey(candidate) === selectedCandidateKey,
    );
  }, [selectableCandidates, selectedCandidateKey]);

  useEffect(() => {
    if (selectedCandidate !== undefined) {
      setActiveContext(getCandidateContextType(selectedCandidate));
    }
  }, [selectedCandidate]);

  const activeCandidates =
    visibleActiveContext === undefined
      ? []
      : candidatesByContext[visibleActiveContext];

  return (
    <div className="border-b bg-background px-3 py-2">
      {availableContexts.length > 0 ? (
        <div
          className="flex items-center gap-1 rounded-md bg-muted p-1"
          role="tablist"
          aria-label="Source context"
        >
          {availableContexts.map((context) => (
            <button
              key={context}
              type="button"
              role="tab"
              aria-selected={visibleActiveContext === context}
              onClick={() => setActiveContext(context)}
              className={cn(
                "min-w-0 flex-1 rounded px-2 py-1 text-xs font-medium transition-colors",
                visibleActiveContext === context
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {CATALOG_PICKER_CONTEXT_LABELS[context]}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Source
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label="Create PM source"
            onClick={onCreatePmSource}
            disabled={!canCreatePmSource || isCreatingPmSource}
            className={cn(
              "inline-flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors",
              "text-muted-foreground hover:bg-muted hover:text-foreground",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
            )}
          >
            {isCreatingPmSource ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            ) : (
              <Plus className="h-3 w-3" aria-hidden="true" />
            )}
            <span>Create PM source</span>
          </button>
          <button
            type="button"
            onClick={onClearSelection}
            className={cn(
              "rounded px-2 py-1 text-xs transition-colors",
              selectedCandidateKey === null
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            None
          </button>
        </div>
      </div>

      {createPmSourceError !== null ? (
        <p className="mt-2 text-xs text-destructive">{createPmSourceError}</p>
      ) : null}

      <div className="mt-2 max-h-24 space-y-1 overflow-y-auto pr-1">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading sources</p>
        ) : error !== null ? (
          <p className="text-xs text-destructive">{error}</p>
        ) : activeCandidates.length === 0 ? (
          <p className="text-xs text-muted-foreground">No sources available</p>
        ) : (
          activeCandidates.map((candidate) => {
            const key = getCatalogSourceCandidateKey(candidate);
            const selected = selectedCandidateKey === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelectCandidate(candidate)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-xs transition-colors",
                  selected
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card hover:bg-accent",
                )}
              >
                <span className="min-w-0 truncate font-medium">
                  {getCandidateLabel(candidate)}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {candidate.capabilities.writable ? "Read/write" : "Read"}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
